import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { TemplateDetails } from '@core/models/case-communication/template-details.model';
import { TemplateInfoDialog, TemplateInfoDialogPost, TemplateInfoForm } from '@core/models/case-communication/template-info-dialog.model';
import { CaseCommunicationService } from '@core/services/case-communication.service';
import { ErrorService, LayoutService } from '@core/services';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { finalize, takeUntil } from 'rxjs/operators';
import { StiiraError } from '@core/models';
import { TemplateDetailsStoreService } from '@core/services/template-details-store.service';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { OwnerOptions } from '@core/enums/owner-options.enum';

@Component({
    selector: 'app-edit-template-info',
    templateUrl: './edit-template-info.component.html',
    styleUrls: ['./edit-template-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditTemplateInfoComponent implements OnInit {
  static readonly componentName = 'EditTemplateInfoComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();
  
  public form: FormGroup<TemplateInfoForm>;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean>;
  public isMobile$: Observable<boolean>;
  
  public position = position;
  public onDragEnded = onDragEnded;

  private formChangeEmitted: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { 
      templateDetails: TemplateDetails; 
      options: TemplateInfoDialog;
      sysText: any; 
      unsavedChangesSysText: any
    },
    private templateInfoDialogRef: MatDialogRef<EditTemplateInfoComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private caseCommunicationService: CaseCommunicationService,
    private store: TemplateDetailsStoreService,
    private errorService: ErrorService,
    private layoutService: LayoutService
    ) { 
    this.isSubmitting$ = new Subject();

    this.form = this.fb.group({
      templateName: this.fb.control(null, Validators.required),
      emailType: this.fb.control(null, Validators.required),
      ownerEmployerId: [null],
      allowUse: [null],
      allowEmployerAssignments: [null]
    });

    if (this.data.templateDetails.isSystem) {
      this.data.options.employers = [ { id: OwnerOptions.System, description: "-- " + this.data.sysText.system + " --" } ];
    }
    else if (this.data.templateDetails.isStock) {
      this.data.options.employers = [ { id: OwnerOptions.Stock, description: "-- " + this.data.sysText.stock + " --" } ];
    }
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();
    closeDialogOnEscape(this.templateInfoDialogRef, this.isSubmitting$, () => this.cancel());

    this.form.patchValue({
      templateName: this.data.templateDetails.name,
      emailType: this.data.templateDetails.emailType.id as number,
      ownerEmployerId: this.data.templateDetails.isSystem
        ? OwnerOptions.System
        : this.data.templateDetails.isStock
          ? OwnerOptions.Stock
          : this.data.templateDetails.ownerEmployerId,
      allowUse: this.data.templateDetails.allowUse,
      allowEmployerAssignments: this.data.templateDetails.allowEmployerAssignments
    });

    if (!this.data.options.canEditName) {
      this.form.controls.templateName.disable();
    }

    if (!this.data.options.canEditType) {
      this.form.controls.emailType.disable();
    }

    if (!this.data.options.canEditOwner) {
      this.form.controls.ownerEmployerId.disable();
    }

    if (!this.data.options.canEditAllowUse) {
      this.form.controls.allowUse.disable();
    }

    if (!this.data.options.canEditAllowEmployerAssignments) {
      this.form.controls.allowEmployerAssignments.disable();
    }

    this.formInitValues = { ...this.form.value };

    this.setFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setFormErrors() {
    setTimeout(() => {
      this.errorService.setFormModelStateErrors(this.form, this.data.templateDetails.missingDetails)
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  public save(): void {
    const dto: TemplateInfoDialogPost = {
      templateId: this.data.templateDetails.id,
      templateName: this.form.controls.templateName.value,
      emailType: this.form.controls.emailType.value,
      ownerEmployerId: this.data.templateDetails.isSystem || this.data.templateDetails.isStock
        ? null
        : this.form.controls.ownerEmployerId.value,
      allowUse: this.form.controls.allowUse.value,
      allowEmployerAssignments: this.form.controls.allowEmployerAssignments.value
    }
      this.caseCommunicationService.postTemplateInfo(dto)
        .pipe(
          indicate(this.isSubmitting$),
          finalize(() => { })
        )
        .subscribe(
          (res) => {
              this.store.templateDetails = res;
              this.templateInfoDialogRef.close(res);
            },
          (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors)
        );
  }

  public cancel(): void {
    if (this.noChanges) {
      this.templateInfoDialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  public openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.templateInfoDialogRef.close();
        }
      });
  }
}
